import { Box, Grid, Typography } from '@mui/material';
import { memo } from 'react';

import {
  convertBuildingAmenity,
  convertBuildingStandard,
  convertElevationGrade,
  convertFencingExtent,
  convertFrontFencing,
  convertLandOccupationType,
  convertLotPosition,
  convertPreservationState,
  convertUnitInfrastructure,
} from '../../../api/characteristics/enumerations';
import {
  HouseEnterprise,
  SectionStatus,
} from '../../../api/characteristics/types';
import { SectionStatusBadge } from '../components/StatusIndicators';
import { AnswerTag, AnswerTagsBox, StyledDivider } from '../styles';

interface HouseEnterpriseSectionProps {
  formData: HouseEnterprise;
  sectionStatus?: SectionStatus;
}

export const HouseEnterpriseSection = memo(function HouseEnterpriseSection({
  formData,
  sectionStatus,
}: HouseEnterpriseSectionProps) {
  return (
    <Box>
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12}>
          <SectionStatusBadge status={sectionStatus} />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="enterprise-dados-gerais"
          >
            Dados Gerais
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Padrão do Empreendimento
              </Typography>
              <Typography>
                {formData.conservation.enterprise_standard
                  ? convertBuildingStandard(
                      formData.conservation.enterprise_standard
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Estado de Conservação
              </Typography>
              <Typography>
                {formData.conservation.preservation_state
                  ? convertPreservationState(
                      formData.conservation.preservation_state
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Controle de Acesso
              </Typography>
              <Typography>
                {formData.conservation.access_control == null
                  ? '-'
                  : String(
                      formData.conservation.access_control ? 'Sim' : 'Não'
                    )}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° de Prédios
              </Typography>
              <Typography>
                {formData.general_info.number_of_buildings ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° Total de Unidades
              </Typography>
              <Typography>
                {formData.general_info.total_units ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° de Unidade de Tipologia
              </Typography>
              <Typography>
                {formData.general_info.number_of_unit_typologies ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Fração Ideal
              </Typography>
              <Typography>
                {formData.general_info.ideal_fraction ?? '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize={18} fontWeight={600} id="enterprise-terreno">
            Terreno
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Posição
              </Typography>
              <Typography>
                {formData.terrain.land_position
                  ? convertLotPosition(formData.terrain.land_position)
                  : '-'}
              </Typography>
            </Grid>
            {formData.terrain.land_position_other && (
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Posição - Outros
                </Typography>
                <Typography>{formData.terrain.land_position_other}</Typography>
              </Grid>
            )}
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Cota/Greide
              </Typography>
              <Typography>
                {formData.terrain.land_grade
                  ? convertElevationGrade(formData.terrain.land_grade)
                  : '-'}
              </Typography>
            </Grid>
            {formData.terrain.land_grade_other && (
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Cota/Greide - Outros
                </Typography>
                <Typography>{formData.terrain.land_grade_other}</Typography>
              </Grid>
            )}
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Área (m²)
              </Typography>
              <Typography>
                {formData.general_info.lot_total_area ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Testada
              </Typography>
              <Typography>{formData.general_info.frontage ?? '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Profundidade equivalente (m)
              </Typography>
              <Typography>
                {formData.general_info.equivalent_depth ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Cercamento
              </Typography>
              <Typography>
                {formData.terrain.fencing
                  ? convertFencingExtent(formData.terrain.fencing)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Tipo Cercamento Frente
              </Typography>
              <Typography>
                {formData.terrain.front_fence
                  ? convertFrontFencing(formData.terrain.front_fence)
                  : '-'}
              </Typography>
            </Grid>
            {formData.terrain.front_fence_other && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Tipo Cercamento Frente - Outros
                </Typography>
                <Typography>{formData.terrain.front_fence_other}</Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Tipo Cercamento Lateral/Fundos
              </Typography>
              <Typography>
                {formData.terrain.side_back_fence
                  ? convertFrontFencing(formData.terrain.side_back_fence)
                  : '-'}
              </Typography>
            </Grid>
            {formData.terrain.side_back_fence_other && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Tipo Cercamento Lateral/Fundos - Outros
                </Typography>
                <Typography>
                  {formData.terrain.side_back_fence_other}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="enterprise-usos-infraestrutura"
          >
            Usos e Infraestrutura
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Ocupação do Terreno</Typography>
              <AnswerTagsBox>
                {(formData.composition.land_occupation ?? []).length > 0 ? (
                  (formData.composition.land_occupation ?? []).map((v) => (
                    <AnswerTag key={v}>
                      {convertLandOccupationType(v)}
                    </AnswerTag>
                  ))
                ) : (
                  <Typography>-</Typography>
                )}
                {formData.composition.land_occupation_other && (
                  <AnswerTag>
                    {formData.composition.land_occupation_other}
                  </AnswerTag>
                )}
              </AnswerTagsBox>
            </Grid>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Infraestrutura</Typography>
              <AnswerTagsBox>
                {(formData.infrastructure.items ?? []).length > 0 ? (
                  (formData.infrastructure.items ?? []).map((v) => (
                    <AnswerTag key={v}>
                      {convertUnitInfrastructure(v)}
                    </AnswerTag>
                  ))
                ) : (
                  <Typography>-</Typography>
                )}
                {formData.infrastructure.items_other && (
                  <AnswerTag>{formData.infrastructure.items_other}</AnswerTag>
                )}
              </AnswerTagsBox>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="enterprise-equipamentos"
          >
            Equipamentos
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <AnswerTagsBox>
            {(formData.equipments.items ?? []).length > 0 ? (
              (formData.equipments.items ?? []).map((v) => (
                <AnswerTag key={v}>{convertBuildingAmenity(v)}</AnswerTag>
              ))
            ) : (
              <Typography>-</Typography>
            )}
            {formData.equipments.other_equipments && (
              <AnswerTag>{formData.equipments.other_equipments}</AnswerTag>
            )}
          </AnswerTagsBox>
          {formData.equipments.embedded_cabinets_rooms != null && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  N° Armários Embutidos
                </Typography>
                <Typography>
                  {formData.equipments.embedded_cabinets_rooms}
                </Typography>
              </Grid>
            </Grid>
          )}
          {formData.equipments.leisure_halls_quantity != null && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  N° Salas de Lazer
                </Typography>
                <Typography>
                  {formData.equipments.leisure_halls_quantity}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="enterprise-dados-administrativos"
          >
            Dados Administrativos
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Taxa de Condomínio
              </Typography>
              <Typography>
                {formData.general_info.condominium_fee ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Administradora do Condomínio
              </Typography>
              <Typography>
                {formData.general_info.condominium_manager ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Telefone Administradora
              </Typography>
              <Typography>
                {formData.general_info.condominium_phone ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Email Administradora
              </Typography>
              <Typography>
                {formData.general_info.condominium_email ?? '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
});
